import { HEADER } from '@opengov/stories-shared/constants';
import { IStoryContent, IStory } from '@opengov/stories-shared/types';
import { DATA_MERGE_TAG_ID, selectNewStoryContents } from '@opengov/stories-shared/utils';
import { orderStoryContents } from '@opengov/stories-shared/utils/general';

import classnames from 'classnames2';
import HTMLReactParser, { attributesToProps, domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import type { Props } from 'html-react-parser/lib/attributes-to-props';
import update from 'immutability-helper';
import { find, isNil } from 'lodash';
import React, { MouseEvent, useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import { IEditorMessages, IModalMessages, ISettingsColors, IUpdateStoryContentProps } from '../../../../../types';
import {
  ADD_EMBED,
  ADD_IMAGE,
  ADD_PROGRESS_TRACKER,
  ADD_TABLE_TILE,
  ADD_TILE,
  ADD_VIDEO,
  DIV,
  SCRIPT
} from '../../../../constants';
import { copyContent } from '../../../../utils';
import { applyBrandingFontColorToContent, IFonts, updateContentFontFamily, updateLogo } from '../../../../utils/dom';
import { ISubtypeColors } from '../../../../utils/editor';

import { LoadingText } from '../../../../common/components/LoadingText/LoadingText';
import { Div, Link, MergeTag, Script } from './components';
import { EditableDiv } from './components/EditableDiv/EditableDiv';
import styles from './tinyMCEStoryEditorUtils.scss';

type MediaHandler = (event: MouseEvent<HTMLAnchorElement>) => void;

type AiGenerationStatus = 'generating' | 'error' | 'complete';
export type EditorValueSource =
  | { variant: 'user-editing' }
  | {
      variant: 'ai-editing';
      status: AiGenerationStatus;
      editorUuid: string;
      resetValueSourceToUser: () => void;
    };

export type AiSummaryResult = { editorUuid: string; status: AiGenerationStatus; value?: string };

export interface IMediaHandlers {
  handleImage: MediaHandler;
  handleVideo: MediaHandler;
  handleEmbed: MediaHandler;
  handleReport: MediaHandler;
  handleReportTable: MediaHandler;
  handleProgressTracker: MediaHandler;
}

interface IRendererArgsAdditionalProps {
  entitySubdomain: string;
  window: Window;
  document: Document;
  storyContent: IStoryContent;
  totalReportTables: number;
  mediaHandlers: IMediaHandlers;
  messages: IEditorMessages;
  modalMessages: IModalMessages;
  readOnly: boolean;
  fontColors: ISubtypeColors;
  handleRemoveSlide(mediaIndex: number): void;
  handleEditMedia(modal: string, mediaIndex: number): void;
  onChange(value: string, uuid: string): void;
  handleGenerateAiSummary(aiSummary: AiSummaryResult): void;
  editorValueSource: EditorValueSource;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  canUseOgAssistGenerateSummary: boolean;
  entityId: number;
  showProgressTracker: boolean;
  updateStoryContent: (storyContent: IStoryContent) => void;
  updateStoryContentDB: (storyContent: IUpdateStoryContentProps) => void;
  story: IStory;
  isTemplate: boolean;
  saveProgressTrackerConfig: (props: { storyId: string; contentId: string; config: any }) => Promise<void>;
  isAiEnabledByAdmin: boolean;
  publicationId: string | undefined;
  isCustomSpreadsheetReportEnabled: boolean;
  contentSeed: number;
}

interface IRendererArgs {
  domNode: Element;
  props: Props;
  parserOptions: HTMLReactParserOptions;
  additionalProps: IRendererArgsAdditionalProps;
  contentHtml: string;
}

const hasClassName = (domNode: Element, className: string): boolean =>
  (domNode.attribs.class ?? '').split(/\s+/).includes(className);

const countDescendantsWithClassName = (domNode: Element, className: string): number =>
  domNode.children.reduce(
    (count, child) =>
      child instanceof Element
        ? count + Number(hasClassName(child, className)) + countDescendantsWithClassName(child, className)
        : count,
    0
  );

/**
 * slick moves the `.gallery-block` slides React rendered under `.slick-gallery` into its own
 * `.slick-list > .slick-track` wrappers, so React can no longer insert or remove individual slides
 * (removeChild throws NotFoundError). Keying the wrapper by slide count makes React replace the whole
 * gallery when a slide is added or removed; the remounted Div then re-initializes slick.
 */
export const getGalleryWrapperKey = (domNode: Element): string | undefined =>
  hasClassName(domNode, 'gallery-wrapper')
    ? `gallery-wrapper-${countDescendantsWithClassName(domNode, 'gallery-block')}`
    : undefined;

const RendererByNodeName = {
  [DIV]: ({ domNode, props, parserOptions, additionalProps, contentHtml }: IRendererArgs) => {
    return (
      <Div
        key={getGalleryWrapperKey(domNode)}
        elementProps={props}
        {...additionalProps}
        contentHtml={contentHtml}
        updateStoryContent={additionalProps.updateStoryContent}
        updateStoryContentDB={additionalProps.updateStoryContentDB}
        story={additionalProps.story}
        storyContent={additionalProps.storyContent}
        isTemplate={additionalProps.isTemplate}
      >
        {domToReact(domNode.children, parserOptions)}
      </Div>
    );
  },
  a: ({ domNode, props, parserOptions }: IRendererArgs) => (
    <Link {...props}>{domToReact(domNode.children, parserOptions)}</Link>
  ),
  [SCRIPT]: ({ domNode, props, parserOptions, additionalProps: { window, document } }: IRendererArgs) => (
    <Script {...props} window={window} document={document}>
      {domToReact(domNode.children, parserOptions)}
    </Script>
  )
};

export const parseHTMLString = (
  contentHtml: string,
  additionalProps: IRendererArgsAdditionalProps
): string | JSX.Element | JSX.Element[] => {
  const parserOptions: HTMLReactParserOptions = {
    htmlparser2: {
      lowerCaseTags: false
    },
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.attribs) {
        const props = attributesToProps(domNode.attribs);

        if (!additionalProps.readOnly && domNode.attribs.class?.indexOf('editable') > -1) {
          const value = extractValue(domNode);
          const isGenerating = determineIsGenerating(additionalProps, props);

          return (
            <EditorContainer
              value={value}
              isGenerating={isGenerating}
              additionalProps={additionalProps}
              baseProps={props}
            />
          );
        }
        if (additionalProps.readOnly && domNode.attribs.class?.indexOf('merge-tag') > -1) {
          return <MergeTag id={props[DATA_MERGE_TAG_ID]} />;
        }
        const renderer = RendererByNodeName[domNode.name];
        if (renderer) {
          return renderer({ domNode, props, parserOptions, additionalProps, contentHtml });
        }
      }
    }
  };

  return HTMLReactParser(contentHtml, parserOptions);
};

function determineIsGenerating(additionalProps: IRendererArgsAdditionalProps, props: Props) {
  return (
    additionalProps.editorValueSource.variant === 'ai-editing' &&
    !isNil(props['data-uuid']) &&
    props['data-uuid'] === additionalProps.editorValueSource.editorUuid &&
    additionalProps.editorValueSource.status === 'generating'
  );
}

function extractValue(domNode: Element) {
  const children = domToReact(domNode.children);
  let value = '';
  if (typeof children === 'string') {
    value = children;
  } else if (Array.isArray(children)) {
    value = children.map((reactNode) => ReactDOMServer.renderToStaticMarkup(reactNode)).join('');
  } else {
    value = ReactDOMServer.renderToStaticMarkup(children);
  }

  return value;
}

function EditorContainer({
  value,
  isGenerating,
  additionalProps,
  baseProps
}: {
  value: string;
  isGenerating: boolean;
  additionalProps: IRendererArgsAdditionalProps;
  baseProps: Props;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(isGenerating);
  useEffect(() => setIsLoading(isGenerating), [isGenerating]);

  return (
    <div className={classnames(isLoading ? styles.loadingWrapper : '')}>
      {isLoading && additionalProps.editorValueSource.variant === 'user-editing' ? (
        <div className={styles.spinnerPlacement}>
          <LoadingText messages={additionalProps.messages} />
        </div>
      ) : null}

      <div className={classnames(isLoading ? styles.loadingEditorOverlay : '')}>
        <EditableDiv
          {...baseProps}
          editorProps={{
            value,
            contentSeed: additionalProps.contentSeed,
            storyContent: additionalProps.storyContent,
            onChange: additionalProps.onChange,
            window: additionalProps.window,
            entitySubdomain: additionalProps.entitySubdomain,
            fontColors: additionalProps.fontColors,
            messages: additionalProps.messages,
            editorValueSource: additionalProps.editorValueSource,
            entityId: additionalProps.entityId,
            setIsLoading: setIsLoading,
            canUseAiToGenerateContent: additionalProps.canUseAiToGenerateContent,
            canUseStoriesSummarizerGA: additionalProps.canUseStoriesSummarizerGA,
            isAiEnabledByAdmin: additionalProps.isAiEnabledByAdmin,
            modalMessages: additionalProps.modalMessages
          }}
        />
      </div>
    </div>
  );
}

export type AddMediaHandler = (event: MouseEvent<HTMLAnchorElement>, modal: string) => void;

/**
 * The team is trying to move away from this compose-app centralized modal handling approach.
 * Colocate your modals and handling logic in the component where it is most relevant. (using local state)
 */
export const getMediaHandlers = (handleAddMedia: AddMediaHandler): IMediaHandlers => ({
  handleImage: (event) => handleAddMedia(event, ADD_IMAGE),
  handleVideo: (event) => handleAddMedia(event, ADD_VIDEO),
  handleEmbed: (event) => handleAddMedia(event, ADD_EMBED),
  handleReport: (event) => handleAddMedia(event, ADD_TILE),
  handleReportTable: (event) => handleAddMedia(event, ADD_TABLE_TILE),
  handleProgressTracker: (event) => handleAddMedia(event, ADD_PROGRESS_TRACKER)
});

export const STORY_CONTENT_ID_PREFIX = 'storyContent-';

export const getVisibleStoryContentId = (document: Document) => {
  const storyContents = document.querySelectorAll(`[id^="${STORY_CONTENT_ID_PREFIX}"]`);
  const [firstVisibleStoryContent] = Array.from(storyContents).filter((storyContent) => {
    const bounds = storyContent.getBoundingClientRect();
    // storyContent is active if at least 50% of it is visible in the viewport
    return bounds.height / 2 + bounds.top >= 0;
  });
  return firstVisibleStoryContent?.getAttribute('id').replace(STORY_CONTENT_ID_PREFIX, '') || null;
};

export const scrollToStoryContent = (document: Document, storyContentId: string) => {
  document.getElementById(`${STORY_CONTENT_ID_PREFIX}${storyContentId}`).scrollIntoView();
};

// We need newStoryContentOrder prop to add the newStoryContent in the correct spot
export const getStoryContentsNextToNewStoryContent = (
  newStoryContentOrder: number,
  storyContents: IStoryContent[]
): IStoryContent[] =>
  storyContents.reduce((newStoryContents, storyContent) => {
    return storyContent.order >= newStoryContentOrder
      ? [...newStoryContents, { ...storyContent, order: storyContent.order + 1 }]
      : newStoryContents;
  }, []);

export const moveStoryContent = (up: boolean, storyContent: IStoryContent, storyContents: IStoryContent[]) => {
  const index = storyContent.order - 1;
  const newIndex = up ? index - 1 : index + 1;
  const newStoryContents = update(storyContents, {
    $splice: [
      [newIndex, 1],
      [index, 0, storyContents[newIndex]]
    ]
  });
  return orderStoryContents(newStoryContents);
};

export const formatStoryContent = (
  storyContent: IStoryContent,
  entityName: string,
  format: string,
  fonts: IFonts,
  colors: ISettingsColors
) => {
  const { _id, type, order, active, content } = storyContent;
  const newStoryContent = find(selectNewStoryContents(), { format });
  const copiedContent = copyContent({ content } as IStoryContent, newStoryContent as IStoryContent);
  const updatedFontContent =
    fonts.headline || fonts.paragraph ? updateContentFontFamily(copiedContent, fonts) : copiedContent;
  const updatedContent = colors ? applyBrandingFontColorToContent(updatedFontContent, colors) : updatedFontContent;
  let newContent = '';
  if (updatedContent) {
    if (entityName && type === HEADER && updatedContent.indexOf('LOGOTYPE') > -1) {
      newContent = updateLogo({ ...newStoryContent, content: updatedContent }, entityName.toUpperCase()).content;
    } else {
      newContent = updatedContent;
    }
  }
  return { ...newStoryContent, _id, order, active, content: newContent };
};
